import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api, setAccessToken } from '@/services/api'

export interface TenantInfo {
  id: number
  nome: string
  plano: string
  status: string
}

interface User {
  id: number
  nome: string
  email: string
  role: 'producer' | 'installer' | 'manager' | 'admin'
  tenantId: number
  tenant?: TenantInfo
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, senha: string) => Promise<void>
  logout: () => Promise<void>
  setSession: (accessToken: string, user: User) => void
  updateUser: (patch: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

function mapUser(raw: {
  id: number
  nome: string
  email: string
  role: User['role']
  tenantId?: number
  tenant?: TenantInfo
}): User {
  return {
    id: raw.id,
    nome: raw.nome,
    email: raw.email,
    role: raw.role,
    tenantId: raw.tenant?.id ?? raw.tenantId ?? 0,
    tenant: raw.tenant,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const hasRefreshCookie = document.cookie.split(';').some(c => c.trim().startsWith('refresh_token='))
    if (!hasRefreshCookie) {
      setIsLoading(false)
      return
    }

    api.post<{ data: { access_token: string } }>('/api/v1/auth/refresh')
      .then(async ({ data }) => {
        setAccessToken(data.data.access_token)
        const me = await api.get<{ data: { user: User & { tenantId: number } } }>('/api/v1/auth/me')
        setUser(mapUser(me.data.data.user))
      })
      .catch(() => { /* sem sessão anterior — ok */ })
      .finally(() => setIsLoading(false))
  }, [])

  function setSession(accessToken: string, user: User) {
    setAccessToken(accessToken)
    setUser(user)
  }

  function updateUser(patch: Partial<User>) {
    setUser(prev => (prev ? { ...prev, ...patch } : null))
  }

  async function login(email: string, senha: string) {
    const { data } = await api.post<{
      data: {
        access_token: string
        user: { id: number; nome: string; email: string; role: User['role']; tenant?: TenantInfo }
      }
    }>('/api/v1/auth/login', { email, senha })

    setAccessToken(data.data.access_token)
    setUser(mapUser(data.data.user))
  }

  async function logout() {
    try { await api.post('/api/v1/auth/logout') } catch {}
    setAccessToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout, setSession, updateUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return ctx
}
